'use client'

import {
      Sidebar,
      SidebarContent,
      SidebarFooter,
      SidebarGroup,
      SidebarGroupContent,
      SidebarGroupLabel,
      SidebarHeader,
      SidebarMenu,
      SidebarMenuButton,
      SidebarMenuItem,
      useSidebar
} from "@/components/ui/sidebar";
import { useAppContext } from "@/hooks/use-app";
import { Utils } from "@/lib/utils";
import { Bell, ChevronRight, Code2, FileText, Home, Layers, MailCheck, Settings, Tags, Users } from "lucide-react";
import { signOut, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo } from "react";
import ProfileAvatar from "../profile-avatar";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Label } from "../ui/label";

export function AppSidebar() {
      const { currentUser } = useAppContext()
      const t = useTranslations("app_sidebar");
      const { data: session } = useSession();
      const { selectedProject, setSelectedProject } = useAppContext();
      const { theme } = useTheme();


      useEffect(() => {
            if (selectedProject) {
                  setSelectedProject(selectedProject);
            }
      }, [selectedProject, setSelectedProject]);

      const { open } = useSidebar();
      // Menu items.
      const projectMenu = [
            {
                  title: t("dashboard"),
                  url: "/",
                  icon: Home,
            },
            {
                  title: t("collaborators"),
                  url: "/collaborators",
                  icon: Users,
            },
            {
                  title: t("tags"),
                  url: "/tags",
                  icon: Tags,
            },
            {
                  title: t("posts"),
                  url: "/posts",
                  icon: FileText,
            },
            {
                  title: t("subscribers"),
                  url: "/subscribers",
                  icon: MailCheck,
            },
            {
                  title: t("news-page"),
                  url: "/news-page",
                  icon: Bell,
            },
            {
                  title: t("widget"),
                  url: "/widget",
                  icon: Code2,
            }
      ]

      const adminMenu = [
            {
                  title: t("home"),
                  url: "/home",
                  icon: Home,
            },
            {
                  title: t("projects"),
                  url: "/projects",
                  icon: Layers,
            },
      ]


      const logoUrl = useMemo(() => {

            let logo = theme === "dark" ? "logo_dark.png" : "logo_light.png"

            if (!open) {
                  logo = "logo_short.png"
            }

            return Utils.image.getImageUrl(`/logo/${logo}`);
      }, [open, theme])


      return (
            <Sidebar variant="sidebar" collapsible="icon">
                  <SidebarHeader>
                        <Image src={logoUrl} alt="Logo" width={open ? 100 : 32} height={32} />
                  </SidebarHeader>
                  <SidebarContent>
                        <SidebarGroup>
                              <SidebarGroupLabel>{t("administration")}</SidebarGroupLabel>
                              <SidebarGroupContent>
                                    <SidebarMenu>
                                          {adminMenu.map((item) => (
                                                <SidebarMenuItem key={item.title}>
                                                      <SidebarMenuButton asChild>
                                                            <Link href={item.url}>
                                                                  <item.icon />
                                                                  <span>{item.title}</span>
                                                            </Link>
                                                      </SidebarMenuButton>
                                                </SidebarMenuItem>
                                          ))}
                                    </SidebarMenu>
                              </SidebarGroupContent>
                        </SidebarGroup>
                        {selectedProject && <SidebarGroup>
                              <SidebarGroupLabel className="flex justify-between">
                                    {selectedProject.name}
                                    {currentUser?.id === selectedProject.ownerId && <Button variant="ghost" size="icon"><Link href={`/projects/${selectedProject.id}/settings`}><Settings /></Link></Button>}
                              </SidebarGroupLabel>
                              <SidebarGroupContent>
                                    <SidebarMenu>
                                          {projectMenu.map((menu) => (
                                                <SidebarMenuItem key={menu.title}>
                                                      <SidebarMenuButton asChild>
                                                            <Link href={`/projects/${selectedProject.id}/${menu.url}`}>
                                                                  <menu.icon />
                                                                  <span>{menu.title}</span>
                                                            </Link>
                                                      </SidebarMenuButton>
                                                </SidebarMenuItem>
                                          ))}
                                    </SidebarMenu>
                              </SidebarGroupContent>
                        </SidebarGroup>}

                  </SidebarContent>
                  <SidebarFooter>
                        <SidebarMenu>
                              <SidebarMenuItem>
                                    <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                                <SidebarMenuButton className="h-12 flex items-center gap-2">
                                                      <ProfileAvatar image={currentUser?.image || ""} name={currentUser?.name || ""} />
                                                      <span>{session?.user?.name || session?.user?.email || ""}</span>
                                                      <ChevronRight className="ml-auto" />
                                                </SidebarMenuButton>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent
                                                side="right"
                                                className="w-[--radix-popper-anchor-width]"
                                          >
                                                <DropdownMenuLabel>
                                                      <Label className="text-sm">{session?.user?.name}</Label>
                                                      <Label className="text-xs text-muted-foreground">{session?.user?.email}</Label>
                                                </DropdownMenuLabel>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem asChild>
                                                      <Link href="/account/settings">
                                                            {t("settings")}
                                                      </Link>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => signOut()}>
                                                      <span>{t("sign_out")}</span>
                                                </DropdownMenuItem>
                                          </DropdownMenuContent>
                                    </DropdownMenu>
                              </SidebarMenuItem>
                        </SidebarMenu>
                  </SidebarFooter>
            </Sidebar>
      )
}
